import { useState, useRef, useEffect } from 'react'
import { MessageCircle, X, Send, Plus, Trash2, History, Loader2, Bot, User, AlertCircle, FileText, Search, ToggleLeft, ToggleRight } from 'lucide-react'
import ReactMarkdown from 'react-markdown'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { ScrollArea } from '@/components/ui/scroll-area'
import { cn } from '@/lib/utils'
import { useAIAssistant, type ChatMessageWithSources } from '@/hooks/useAIAssistant'

export function AIChatbot() {
  const [isOpen, setIsOpen] = useState(false)
  const [input, setInput] = useState('')
  const [showHistory, setShowHistory] = useState(false)
  const scrollRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  const {
    messages,
    isLoading,
    isSearchingDocuments,
    enableRAG,
    setEnableRAG,
    error,
    sendMessage,
    newChat,
    sessions,
    loadSessions,
    loadSession,
    deleteSession,
  } = useAIAssistant()

  // Scroll to bottom when messages change
  useEffect(() => {
    const viewport = scrollRef.current?.querySelector('[data-radix-scroll-area-viewport]')
    if (viewport) {
      viewport.scrollTop = viewport.scrollHeight
    }
  }, [messages])

  // Focus input when chat opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus()
    }
  }, [isOpen])

  // Load sessions when history panel opens
  useEffect(() => {
    if (showHistory) {
      loadSessions()
    }
  }, [showHistory, loadSessions])

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (input.trim() && !isLoading) {
      sendMessage(input)
      setInput('')
    }
  }

  const handleNewChat = () => {
    newChat()
    setShowHistory(false)
  }

  const formatTime = (date: Date) => {
    return new Date(date).toLocaleTimeString('en-GB', {
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  const formatDate = (date: Date) => {
    return new Date(date).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
    })
  }

  // Quick action suggestions
  const suggestions = [
    'What needs to be done next?',
    'Show me suppliers with issues',
    'Any overdue actions?',
    'Summarise recent activity',
  ]

  return (
    <>
      {/* Floating Button */}
      <Button
        onClick={() => setIsOpen(true)}
        className={cn(
          'fixed bottom-20 right-4 sm:bottom-6 sm:right-6 h-14 w-14 rounded-full shadow-lg z-[60] transition-all',
          'bg-certify-600 hover:bg-certify-700 hover:scale-110',
          isOpen && 'hidden'
        )}
        size="icon"
        aria-label="Open AI Assistant"
      >
        <MessageCircle className="h-6 w-6" />
      </Button>

      {/* Chat Panel */}
      <div
        className={cn(
          'fixed z-[60] flex flex-col bg-background shadow-2xl transition-[transform,opacity] duration-300 ease-in-out',
          'bottom-0 left-0 right-0 h-[85vh] rounded-t-2xl border-t border-x',
          'md:left-auto md:bottom-6 md:right-6 md:h-[600px] md:max-h-[80vh] md:w-[400px] md:rounded-lg md:border',
          isOpen ? 'translate-y-0 md:translate-x-0 opacity-100' : 'translate-y-full md:translate-y-0 md:translate-x-full opacity-0 pointer-events-none'
        )}
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b px-4 py-3 bg-muted/50">
          <div className="flex items-center gap-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-certify-600">
              <Bot className="h-4 w-4 text-white" />
            </div>
            <div>
              <h3 className="font-semibold text-sm">LogiDocs Assistant</h3>
              <p className="text-xs text-muted-foreground">Ask me anything about your data</p>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={() => setShowHistory(!showHistory)}
              title="Chat history"
            >
              <History className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={handleNewChat}
              title="New chat"
            >
              <Plus className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={() => setIsOpen(false)}
              title="Close"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* History Panel */}
        {showHistory ? (
          <div className="flex-1 overflow-hidden">
            <div className="p-3 border-b">
              <h4 className="font-medium text-sm">Chat History</h4>
            </div>
            <ScrollArea className="h-[calc(100%-48px)]">
              <div className="p-2 space-y-1">
                {sessions.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-8">
                    No previous chats
                  </p>
                ) : (
                  sessions.map(session => (
                    <div
                      key={session.id}
                      className="flex items-center justify-between p-2 rounded-md hover:bg-muted cursor-pointer group"
                      onClick={() => {
                        loadSession(session)
                        setShowHistory(false)
                      }}
                    >
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{session.title}</p>
                        <p className="text-xs text-muted-foreground">
                          {formatDate(session.updatedAt)}
                        </p>
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6 opacity-0 group-hover:opacity-100"
                        onClick={(e) => {
                          e.stopPropagation()
                          deleteSession(session.id)
                        }}
                      >
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                  ))
                )}
              </div>
            </ScrollArea>
          </div>
        ) : (
          <>
            {/* Messages */}
            <ScrollArea className="flex-1 p-4" ref={scrollRef}>
              {messages.length === 0 ? (
                <div className="space-y-4 pt-4">
                  <div className="text-center">
                    <Bot className="h-12 w-12 mx-auto text-muted-foreground mb-3" />
                    <h4 className="font-medium">How can I help you?</h4>
                    <p className="text-sm text-muted-foreground mt-1">
                      Ask me about your compliance data, suppliers, components, and more.
                    </p>
                  </div>
                  <div className="space-y-2">
                    <p className="text-xs text-muted-foreground font-medium">Quick actions:</p>
                    <div className="flex flex-wrap gap-2">
                      {suggestions.map((suggestion, i) => (
                        <Button
                          key={i}
                          variant="outline"
                          size="sm"
                          className="text-xs h-auto py-1.5"
                          onClick={() => {
                            setInput(suggestion)
                            inputRef.current?.focus()
                          }}
                        >
                          {suggestion}
                        </Button>
                      ))}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  {messages.map((message: ChatMessageWithSources) => (
                    <div key={message.id}>
                      <div
                        className={cn(
                          'flex gap-3',
                          message.role === 'user' ? 'justify-end' : 'justify-start'
                        )}
                      >
                        {message.role === 'assistant' && (
                          <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-certify-600">
                            <Bot className="h-4 w-4 text-white" />
                          </div>
                        )}
                        <div
                          className={cn(
                            'rounded-lg px-3 py-2 max-w-[80%] text-sm',
                            message.role === 'user'
                              ? 'bg-certify-600 text-white'
                              : 'bg-muted'
                          )}
                        >
                          {message.role === 'assistant' ? (
                            <div className="chat-markdown prose prose-sm dark:prose-invert max-w-none prose-p:my-1 prose-ul:my-1 prose-ol:my-1 prose-li:my-0.5 prose-headings:my-2 prose-headings:text-sm prose-strong:text-inherit">
                              <ReactMarkdown>{message.content}</ReactMarkdown>
                            </div>
                          ) : (
                            <p className="whitespace-pre-wrap">{message.content}</p>
                          )}
                          <p
                            className={cn(
                              'text-[10px] mt-1',
                              message.role === 'user'
                                ? 'text-white/70'
                                : 'text-muted-foreground'
                            )}
                          >
                            {formatTime(message.timestamp)}
                          </p>
                        </div>
                        {message.role === 'user' && (
                          <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary">
                            <User className="h-4 w-4 text-primary-foreground" />
                          </div>
                        )}
                      </div>
                      {/* Sources section for assistant messages with document references */}
                      {message.role === 'assistant' && message.sources && message.sources.length > 0 && (
                        <div className="ml-10 mt-1 mb-1">
                          <div className="flex flex-wrap gap-1.5 items-center">
                            <span className="text-[10px] text-muted-foreground font-medium">Sources:</span>
                            {message.sources.map((source) => (
                              <span
                                key={source.id}
                                className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-certify-100 dark:bg-certify-900/30 text-certify-700 dark:text-certify-300 text-[10px] font-medium"
                                title={`${source.name}${source.type ? ` (${source.type})` : ''}`}
                              >
                                <FileText className="h-2.5 w-2.5" />
                                <span className="max-w-[120px] truncate">{source.name}</span>
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                  {isLoading && (
                    <div className="flex gap-3">
                      <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-certify-600">
                        <Bot className="h-4 w-4 text-white" />
                      </div>
                      <div className="bg-muted rounded-lg px-3 py-2 flex items-center gap-2">
                        {isSearchingDocuments ? (
                          <>
                            <Search className="h-4 w-4 animate-pulse text-certify-600" />
                            <span className="text-xs text-muted-foreground">Searching documents...</span>
                          </>
                        ) : (
                          <>
                            <Loader2 className="h-4 w-4 animate-spin" />
                            <span className="text-xs text-muted-foreground">Thinking...</span>
                          </>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </ScrollArea>

            {/* Error Display */}
            {error && (
              <div className="px-4 py-2 bg-destructive/10 border-t border-destructive/20">
                <div className="flex items-center gap-2 text-destructive text-sm">
                  <AlertCircle className="h-4 w-4" />
                  <p>{error}</p>
                </div>
              </div>
            )}

            {/* Input */}
            <div className="border-t">
              <div className="flex items-center justify-between px-3 pt-2 pb-1">
                <button
                  type="button"
                  onClick={() => setEnableRAG(!enableRAG)}
                  className={cn(
                    'flex items-center gap-1.5 text-[10px] font-medium transition-colors',
                    enableRAG ? 'text-certify-600' : 'text-muted-foreground'
                  )}
                  title={enableRAG ? 'Document search enabled' : 'Document search disabled'}
                >
                  {enableRAG ? (
                    <ToggleRight className="h-3.5 w-3.5" />
                  ) : (
                    <ToggleLeft className="h-3.5 w-3.5" />
                  )}
                  Search documents
                </button>
              </div>
              <form onSubmit={handleSubmit} className="px-3 pb-3">
                <div className="flex gap-2">
                  <Input
                    ref={inputRef}
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    placeholder="Ask a question..."
                    disabled={isLoading}
                    className="flex-1"
                  />
                  <Button
                    type="submit"
                    size="icon"
                    disabled={!input.trim() || isLoading}
                    className="bg-certify-600 hover:bg-certify-700"
                  >
                    <Send className="h-4 w-4" />
                  </Button>
                </div>
              </form>
            </div>
          </>
        )}
      </div>
    </>
  )
}
